import { ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageLayout } from '@xtnd/web/shared/layout';
import { TabNavigation, type Tab } from '@xtnd/web/shared/components';
import { useReportFeatureEnabled } from '../../../hooks';
import { REPORTS_ROUTES } from '@xtnd/web/shared/constants';

interface MatterStatementLayoutProps {
  children: ReactNode;
}

export function MatterStatementLayout({ children }: MatterStatementLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const { isLoading, error, refetch, isReportingEnabled } = useReportFeatureEnabled();

  const isDisabled = !isReportingEnabled;

  // Determine active tab from URL path
  const getActiveTab = (): 'dashboard' | 'history' => {
    if (location.pathname.includes('/history')) {
      return 'history';
    }
    return 'dashboard';
  };

  const activeTab = getActiveTab();

  const handleTabChange = (tabId: string) => {
    if (tabId === 'history') {
      navigate(REPORTS_ROUTES.MATTER_STATEMENT.HISTORY, { replace: true });
    } else {
      navigate(REPORTS_ROUTES.MATTER_STATEMENT.DASHBOARD, { replace: true });
    }
  };

  const tabs: Tab[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: 'HiChartBar',
    },
    {
      id: 'history',
      label: 'History',
      icon: 'HiClock',
    },
  ];

  return (
    <PageLayout
      title="Matter Statement Report"
      description="Generate comprehensive account statements for matters"
      icon="HiDocumentText"
      isLoading={isLoading}
      isDisabled={isDisabled}
      isError={!!error}
      errorState={{
        title: 'Failed to Load Report Configuration',
        description:
          error?.message ?? 'An unknown error occurred while loading the report configuration.',
        onRetry: () => refetch(),
      }}
      disabledState={{
        title: 'Matter Statement Report Disabled',
        description:
          'The Matter Statement Report is not enabled for this tenant. Please contact your administrator to enable this reporting feature.',
      }}
      skeletonProps={{
        summaryCardCount: 3,
        tableRows: 10,
        tableColumns: 7,
      }}
    >
      <div className="space-y-6">
        <TabNavigation tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="mt-6">{children}</div>
      </div>
    </PageLayout>
  );
}

